<template>
  <o-table
    :items="rows"
    :loading="loading"
    :draggable="false"
    detailed
    detail-key="id"
    :show-detail-icon="true"
  >
    <b-table-column v-slot="{ row }" field="liveName" label="Live">
      <router-link
        target="_blank"
        :to="{ name: 'live.edit.informations', params: { id: row.liveId, tab: 'review' } }"
      >
        {{ row.liveName }}
      </router-link>
    </b-table-column>
    <b-table-column v-slot="{ row }" field="dates" label="Dates">
      {{ row.dates }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="vehicleNumber" label="Dossard">
      {{ row.vehicleNumber ? `N°${row.vehicleNumber}` : EMPTY_VALUE }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="initialPercentage" label="Batterie initiale">
      <span
        v-if="row.initialPercentage !== null"
        :class="initialBatteryClass(row.initialPercentage)"
      >
        {{ row.initialPercentage }} %
      </span>
      <template v-else>{{ EMPTY_VALUE }}</template>
    </b-table-column>
    <b-table-column v-slot="{ row }" field="averageMotionConsumptionPerHour" label="Conso / h">
      {{ formatRate(row.averageMotionConsumptionPerHour) }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="averageIdleConsumptionPerHour" label="Conso veille / h">
      {{ formatRate(row.averageIdleConsumptionPerHour) }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="chargeCount" label="Recharges">
      {{ row.chargeCount === null ? EMPTY_VALUE : row.chargeCount }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="averageMotionIntervalSeconds" label="Intervalle moyen">
      {{ formatInterval(row.averageMotionIntervalSeconds) }}
    </b-table-column>

    <template #detail="{ row }">
      <neyos-tracker-statistics-stage-table v-if="row.hasStatistics" :rows="row.stageRows" />
      <p v-else class="has-text-grey is-size-7">Pas de statistiques calculées.</p>
    </template>

    <template #empty>
      <div class="has-text-grey has-text-centered">Aucune statistique sur ces lives.</div>
    </template>
  </o-table>
</template>

<script>
import { format } from 'date-fns'
import OTable from '@components/Table.vue'
import NeyosTrackerStatisticsStageTable from './NeyosTrackerStatisticsStageTable.vue'

const EMPTY_VALUE = '—'

const DATE_FORMAT = 'dd/MM/yyyy'

const INITIAL_BATTERY_TIERS = [
  { above: 90, className: 'has-text-green' },
  { above: 80, className: 'has-text-yellow' },
  { above: 70, className: 'has-text-orange' },
]

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 60 * 60

const hasStatistics = (statistics) =>
  statistics.some(
    ({ batteryStatistics }) =>
      batteryStatistics.finalPercentage !== null || batteryStatistics.motionSeconds > 0,
  )

const formatDuration = (seconds) => {
  const rounded = Math.round(seconds)

  if (rounded < SECONDS_PER_MINUTE) {
    return `${rounded} s`
  }

  if (rounded < SECONDS_PER_HOUR) {
    return `${Math.floor(rounded / SECONDS_PER_MINUTE)} min`
  }

  const hours = Math.floor(rounded / SECONDS_PER_HOUR)
  const minutes = Math.floor((rounded % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)

  return `${hours} h ${String(minutes).padStart(2, '0')}`
}

export default {
  name: 'NeyosTrackerStatisticsTable',

  components: {
    OTable,
    NeyosTrackerStatisticsStageTable,
  },

  props: {
    entries: {
      type: Array,
      required: true,
      validator: (v) =>
        v.every((entry) =>
          [
            'live',
            'vehicleNumber',
            'stages',
            'statistics',
            'aggregates',
            'informationLoading',
          ].every((key) => key in entry),
        ),
    },
  },

  computed: {
    loading() {
      return this.entries.some((entry) => entry.informationLoading)
    },

    rows() {
      return this.entries.map((entry) => {
        const stagesById = Object.fromEntries(entry.stages.map((stage) => [stage.id, stage]))

        const stageRows = [...entry.statistics]
          .sort(
            (left, right) =>
              (stagesById[left.liveStageId]?.startedAt ?? 0) -
              (stagesById[right.liveStageId]?.startedAt ?? 0),
          )
          .map((statistics) => {
            const stage = stagesById[statistics.liveStageId]
            const battery = statistics.batteryStatistics

            return {
              id: statistics.liveStageId,
              stageName: stage ? stage.name : EMPTY_VALUE,
              stageStartedAt: stage ? format(stage.startedAt, DATE_FORMAT) : EMPTY_VALUE,
              stageStartedAtTs: stage ? stage.startedAt.getTime() : 0,
              initialPercentage: battery.initialPercentage,
              finalPercentage: battery.finalPercentage,
              averageMotionConsumptionPerHour: battery.averageMotionConsumptionPerHour,
              averageIdleConsumptionPerHour: battery.averageIdleConsumptionPerHour,
              chargeCount: battery.chargeCount,
              averageMotionIntervalSeconds: battery.averageMotionIntervalSeconds,
            }
          })

        return {
          id: entry.live.id,
          liveId: entry.live.id,
          liveName: entry.live.name,
          dates: `${format(entry.live.startedAt, DATE_FORMAT)} → ${format(
            entry.live.endedAt,
            DATE_FORMAT,
          )}`,
          vehicleNumber: entry.vehicleNumber,
          ...entry.aggregates,
          hasStatistics: hasStatistics(entry.statistics),
          stageRows,
        }
      })
    },
  },

  created() {
    this.EMPTY_VALUE = EMPTY_VALUE
  },

  methods: {
    initialBatteryClass(percentage) {
      return (
        INITIAL_BATTERY_TIERS.find((tier) => percentage > tier.above)?.className ?? 'has-text-red'
      )
    },

    formatRate(percentagePerHour) {
      return percentagePerHour === null ? EMPTY_VALUE : `${percentagePerHour.toFixed(1)} %`
    },

    formatInterval(seconds) {
      return seconds === null ? EMPTY_VALUE : formatDuration(seconds)
    },
  },
}
</script>
